import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, XCircle, AlertTriangle, Calendar, Monitor, Smartphone, HardDrive, Car, Tag, CreditCard, Package } from 'lucide-react';
import { FileText, X, User, MessageSquare, Key, DollarSign } from './icons';
import { useNotificationContext } from '../contexts/NotificationContext';
import { pazYSalvoAPI } from '../services/api';

export default function PazYSalvoSignatureModal({
    pazYSalvo,
    userRole,
    isOpen,
    onClose,
    onSuccess,
    config
}) {
    const [checklistState, setChecklistState] = useState({});
    const [observations, setObservations] = useState('');
    const [loading, setLoading] = useState(false);
    const [allChecked, setAllChecked] = useState(false);
    const { alertSuccess, alertError } = useNotificationContext();

    // Mapeo de roles del frontend a los roles esperados por el backend
    const mapRoleToBackend = (role) => {
        const mapping = {
            'TI': 'Informatica',
            'Jefatura Directa': 'JefeInmediato',
            'Gerencia Finanzas': 'GerenciaFinanzas',
            'Gerente General': 'GerenteGeneral',
            'Contabilidad': 'Contabilidad',
            'RRHH': 'RRHH'
        };
        return mapping[role] || role;
    };


    // Inicializar estado del checklist
    useEffect(() => {
        if (config?.checklistItems && isOpen) {
            const initialState = {};
            config.checklistItems.forEach((_, index) => {
                initialState[index] = false;
            });
            setChecklistState(initialState);
            setAllChecked(false);
            setObservations('');
        }
    }, [config, isOpen]);

    // Verificar si todos los items están marcados
    useEffect(() => {
        if (config?.checklistItems) {
            const totalItems = config.checklistItems.length;
            const checkedItems = Object.values(checklistState).filter(Boolean).length;
            setAllChecked(totalItems > 0 && checkedItems === totalItems);
        }
    }, [checklistState, config]);

    const handleChecklistChange = (index, checked) => {
        setChecklistState(prev => ({
            ...prev,
            [index]: checked
        }));
    };

    const handleSign = async () => {
        if (!allChecked) {
            alertError('Debe completar todos los elementos del checklist antes de firmar');
            return;
        }

        setLoading(true);
        try {
            const backendRole = mapRoleToBackend(userRole);

            // El backend identifica al firmante por el usuario autenticado
            // (req.user.id) -- no hace falta (ni acepta) mandarlo en el body.
            await pazYSalvoAPI.firmar(pazYSalvo.id, backendRole, {
                comentario: observations.trim() || undefined
            });

            alertSuccess(`Documento firmado exitosamente como ${userRole}`);
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Error al firmar:', error.message);
            alertError(error.response?.data?.message || error.message || 'Error al firmar el documento');
        } finally {
            setLoading(false);
        }
    };

    const handleReject = async () => {
        if (!observations.trim()) {
            alertError('Debe proporcionar observaciones para rechazar el documento');
            return;
        }

        setLoading(true);
        try {
            const backendRole = mapRoleToBackend(userRole);

            await pazYSalvoAPI.rechazar(pazYSalvo.id, backendRole, {
                motivo: observations.trim()
            });

            alertSuccess(`Documento rechazado como ${userRole}`);
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Error al rechazar:', error.message);
            alertError(error.response?.data?.message || error.message || 'Error al rechazar el documento');
        } finally {
            setLoading(false);
        }
    };

    const getCategoriaIcon = (categoria, clase) => {
        const cat = categoria?.toLowerCase() || '';

        // Iconos por clase y categoría (reutilizados de MiEquipo.jsx)
        if (clase === 'Operacional') {
            if (cat.includes('vehiculo') || cat.includes('auto') || cat.includes('camion')) return <Car className="w-4 h-4" />;
            if (cat.includes('llave') || cat.includes('acceso')) return <Key className="w-4 h-4" />;
            if (cat.includes('tag')) return <Tag className="w-4 h-4" />;
            if (cat.includes('credencial')) return <CreditCard className="w-4 h-4" />;
            if (cat.includes('caja')) return <DollarSign className="w-4 h-4" />;
            return <Package className="w-4 h-4" />;
        }

        switch (cat) {
            case 'notebook':
            case 'desktop':
            case 'computador':
                return <Monitor className="w-4 h-4" />;
            case 'celular':
            case 'tablet':
                return <Smartphone className="w-4 h-4" />;
            default:
                return <HardDrive className="w-4 h-4" />;
        }
    };


    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Bloquear scroll del body cuando el modal está abierto
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen || !pazYSalvo) return null;

    const isDraft = pazYSalvo.estado === 'Borrador';

    return createPortal(
        <div
            className="fixed inset-0 bg-black bg-opacity-60 z-[9999] overflow-y-auto"
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            <div className="flex min-h-full items-start justify-center p-4 pt-8 pb-8">
                <div
                    className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-4xl"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header con gradiente */}
                    <div className={`bg-gradient-to-r ${isDraft
                        ? 'from-yellow-600 to-orange-600'
                        : 'from-blue-600 to-blue-800'
                        } text-white p-6 rounded-t-xl`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                {isDraft ? <AlertTriangle className="h-8 w-8" /> : <FileText className="h-8 w-8" />}
                                <div>
                                    <h2 className="text-2xl font-bold">{isDraft ? 'Documento en Borrador' : 'Firmar Paz y Salvo'}</h2>
                                    <p className="text-blue-100 mt-1">
                                        {isDraft ? 'Este documento aún no está listo para firmas' : `Como ${userRole}`}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-white/10 rounded-full transition-colors"
                            >
                                <X className="h-6 w-6" />
                            </button>
                        </div>
                    </div>

                    {isDraft ? (
                        <div className="p-8 flex flex-col items-center justify-center text-center">
                            <div className="bg-yellow-100 dark:bg-yellow-900/20 p-6 rounded-full mb-6">
                                <AlertTriangle className="h-16 w-16 text-yellow-600 dark:text-yellow-400" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                                El documento está en estado de Borrador
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 max-w-md mb-8">
                                Este documento de Paz y Salvo aún no ha sido iniciado. Debe ser enviado al flujo de firmas por el administrador o RRHH antes de que puedas firmarlo.
                            </p>
                            <button
                                onClick={onClose}
                                className="px-6 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg font-medium transition-colors"
                            >
                                Entendido, cerrar
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="p-6 max-h-[70vh] overflow-y-auto">
                                {/* Información del empleado */}
                                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="flex items-center space-x-2">
                                            <User className="h-5 w-5 text-gray-500" />
                                            <div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">Empleado</p>
                                                <p className="font-medium text-gray-900 dark:text-white">
                                                    {pazYSalvo.usuarioNombre}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <FileText className="h-5 w-5 text-gray-500" />
                                            <div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">RUT</p>
                                                <p className="font-medium text-gray-900 dark:text-white">
                                                    {pazYSalvo.usuarioRut}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <Calendar className="h-5 w-5 text-gray-500" />
                                            <div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">Fecha Creación</p>
                                                <p className="font-medium text-gray-900 dark:text-white">
                                                    {formatDate(pazYSalvo.fechaCreacion)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Vista de Activos Asignados */}
                                {['Informatica', 'JefeInmediato', 'Contabilidad', 'GerenciaFinanzas', 'RRHH'].includes(mapRoleToBackend(userRole)) && pazYSalvo.activosSnapshot?.length > 0 && (
                                    <div className="mb-8">
                                        <div className="flex items-center space-x-2 mb-4">
                                            <Package className="h-6 w-6 text-blue-600" />
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                Activos por Devolver / Gestionar
                                            </h3>
                                        </div>

                                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                                            <div className="flex items-start space-x-2">
                                                <AlertTriangle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                                                <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                                                    Disclaimer: Al firmar, usted certifica que los activos aquí listados han sido devueltos a la empresa o se encuentran debidamente gestionados conforme a la política interna.
                                                </p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            {pazYSalvo.activosSnapshot.map((activo, idx) => (
                                                <div
                                                    key={idx}
                                                    className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm"
                                                >
                                                    <div className={`p-2 rounded-lg ${activo.clase === 'Operacional' ? 'bg-emerald-100/50 text-emerald-600' : 'bg-blue-100/50 text-blue-600'}`}>
                                                        {getCategoriaIcon(activo.categoria, activo.clase)}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between">
                                                            <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-tight">
                                                                {activo.clase} · {activo.categoria}
                                                            </p>
                                                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 font-bold">
                                                                {activo.estadoActivo}
                                                            </span>
                                                        </div>
                                                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                                            {activo.nombre}
                                                        </p>
                                                        <p className="text-[11px] text-gray-500 dark:text-gray-400 font-mono">
                                                            CÓD: {activo.codigo || 'S/N'}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Checklist obligatorio */}
                                <div className="mb-6">
                                    <div className="flex items-center space-x-2 mb-4">
                                        <CheckCircle className="h-6 w-6 text-blue-600" />
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                            Verificaciones Obligatorias
                                        </h3>
                                        <span className="text-sm text-red-600 font-medium">*Requerido</span>
                                    </div>

                                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
                                        <div className="flex items-start space-x-2">
                                            <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                                                    Importante: Debe completar todas las verificaciones antes de firmar
                                                </p>
                                                <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                                                    Su firma digital certifica que ha completado todos los controles requeridos.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        {config?.checklistItems?.map((item, index) => (
                                            <div key={index} className="flex items-start space-x-3 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                                                <input
                                                    type="checkbox"
                                                    id={`checklist-${index}`}
                                                    checked={checklistState[index] || false}
                                                    onChange={(e) => handleChecklistChange(index, e.target.checked)}
                                                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                />
                                                <label
                                                    htmlFor={`checklist-${index}`}
                                                    className="flex-1 text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                                                >
                                                    {item}
                                                </label>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Indicador de progreso */}
                                    <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-gray-600 dark:text-gray-400">
                                                Progreso del checklist:
                                            </span>
                                            <span className={`font-medium ${allChecked ? 'text-green-600' : 'text-orange-600'}`}>
                                                {Object.values(checklistState).filter(Boolean).length} / {config?.checklistItems?.length || 0} completado
                                            </span>
                                        </div>
                                        <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                            <div
                                                className={`h-2 rounded-full transition-all duration-300 ${allChecked ? 'bg-green-500' : 'bg-orange-400'}`}
                                                style={{
                                                    width: `${config?.checklistItems?.length > 0 ? (Object.values(checklistState).filter(Boolean).length / config.checklistItems.length) * 100 : 0}%`
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Campo de observaciones */}
                                <div className="mb-6">
                                    <div className="flex items-center space-x-2 mb-3">
                                        <MessageSquare className="h-5 w-5 text-gray-500" />
                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Observaciones (opcional)
                                        </label>
                                    </div>
                                    <textarea
                                        value={observations}
                                        onChange={(e) => setObservations(e.target.value)}
                                        placeholder="Agregar comentarios adicionales..."
                                        rows={3}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white resize-none"
                                    />
                                    {observations.trim() && (
                                        <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-sm">
                                            <span className="text-blue-700 dark:text-blue-300">
                                                <strong>Vista previa:</strong> {observations.trim()}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Footer con acciones */}
                            <div className="bg-gray-50 dark:bg-gray-800 px-6 py-4 rounded-b-xl">
                                <div className="flex justify-end">
                                    {/* Botones de acción */}
                                    <div className="flex space-x-3">
                                        <button
                                            onClick={onClose}
                                            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            onClick={handleReject}
                                            disabled={loading}
                                            className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <XCircle className="h-4 w-4" />
                                            <span>{loading ? 'Rechazando...' : (config?.rejectText || 'Rechazar')}</span>
                                        </button>
                                        <button
                                            onClick={handleSign}
                                            disabled={loading || !allChecked}
                                            className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <CheckCircle className="h-4 w-4" />
                                            <span>{loading ? 'Firmando...' : (config?.approvalText || 'Firmar')}</span>
                                        </button>
                                    </div>
                                </div>

                                {!allChecked && (
                                    <div className="mt-3 text-center">
                                        <p className="text-sm text-red-600 dark:text-red-400">
                                            ⚠️ Complete todas las verificaciones para habilitar la firma
                                        </p>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}
